/**
 * Industry → Video Type catalog for the "Generate concepts for video" flow.
 *
 * Each industry has a display name, icon (emoji), short description, benefit
 * line, and an ordered list of video types. Every video type has a name,
 * description, and benefit line. The exact copy is authored by the product
 * team — if you need to tweak wording, edit this file only.
 *
 * Ordering inside each array is significant (matches the marketing spec).
 */

export const CONCEPT_CATALOG = [
    {
        id: 'd2c-commerce',
        icon: '🛍️',
        name: 'D2C Commerce',
        desc: 'Brands selling products directly online',
        benefit: 'Highly dependent on content + ads for sales',
        videoTypes: [
            { id: 'ugc-ads', name: 'UGC Ads', desc: '"Shot-on-phone" style videos that feel like real customer experiences', benefit: 'High-performing ads' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Fast, trend-based short videos with strong hooks', benefit: 'Drives reach & traffic' },
            { id: 'transform-videos', name: 'Transform Videos', desc: 'Before → process → after showing clear results', benefit: 'Strong conversion' },
            { id: 'product-demo-videos', name: 'Product Demo Videos', desc: 'Shows how the product works and key features', benefit: 'Boosts website conversions' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer experiences and results', benefit: 'Builds trust' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Discounts, launches, limited-time deals', benefit: 'Drives immediate sales' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Story-driven, high-quality product storytelling', benefit: 'Builds brand value' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Custom formats based on campaign needs', benefit: 'Flexible use cases' },
        ],
    },
    {
        id: 'fashion-brands',
        icon: '👗',
        name: 'Fashion Brands',
        desc: 'Clothing, apparel, and accessories',
        benefit: 'Driven by trends, aesthetics, and aspiration',
        videoTypes: [
            { id: 'lookbook-videos', name: 'Lookbook Videos', desc: 'Showcases outfits in styled shoots', benefit: 'Builds aspiration' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Trend-based fashion content', benefit: 'Drives reach' },
            { id: 'transform-videos', name: 'Transform Videos', desc: 'Outfit transitions and styling changes', benefit: 'High retention' },
            { id: 'lifestyle-videos', name: 'Lifestyle Videos', desc: 'Shows outfits in real-life situations', benefit: 'Relatable & desirable' },
            { id: 'product-showcase-videos', name: 'Product Showcase Videos', desc: 'Model/studio-based product display', benefit: 'Improves purchase confidence' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer try-ons and feedback', benefit: 'Builds trust' },
            { id: 'commercial-fashion-film', name: 'Commercial / Fashion Film', desc: 'Cinematic storytelling for brand identity', benefit: 'Premium positioning' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Seasonal or campaign content', benefit: 'Creative flexibility' },
        ],
    },
    {
        id: 'beauty-brands',
        icon: '💄',
        name: 'Beauty Brands',
        desc: 'Skincare, cosmetics, grooming',
        benefit: 'Driven by results, trust, and visual proof',
        videoTypes: [
            { id: 'transform-videos', name: 'Transform Videos', desc: 'Before → after showing visible results', benefit: 'Strong conversion' },
            { id: 'tutorial-videos', name: 'Tutorial Videos', desc: 'Step-by-step product usage', benefit: 'Builds trust' },
            { id: 'ugc-ads', name: 'UGC Ads', desc: 'Real user-style reviews', benefit: 'High-performing ads' },
            { id: 'satisfying-asmr', name: 'Satisfying / ASMR Videos', desc: 'Close-up textures and application visuals', benefit: 'Increases watch time' },
            { id: 'product-demo-videos', name: 'Product Demo Videos', desc: 'Shows application and usage clearly', benefit: 'Reduces hesitation' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Real user results and experiences', benefit: 'Builds credibility' },
            { id: 'commercial-beauty-ad', name: 'Commercial / Beauty Ad', desc: 'High-end brand storytelling', benefit: 'Brand recall' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Influencer or campaign-based content', benefit: 'Flexible execution' },
        ],
    },
    {
        id: 'local-businesses',
        icon: '🏪',
        name: 'Local Businesses',
        desc: 'Restaurants, salons, clinics, shops',
        benefit: 'Driven by local discovery + quick decisions',
        videoTypes: [
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Local discovery and viral content', benefit: 'Drives footfall' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Promotions and deals', benefit: 'Immediate conversions' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer experiences and reactions', benefit: 'Builds trust' },
            { id: 'bts-videos', name: 'BTS Videos', desc: 'Behind-the-scenes of service/product', benefit: 'Authenticity' },
            { id: 'product-service-demo', name: 'Product / Service Demo Videos', desc: 'Shows what customers can expect', benefit: 'Improves decision making' },
            { id: 'ugc-ads', name: 'UGC Ads', desc: 'Local customer-style content', benefit: 'High engagement' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Story-driven brand introduction', benefit: 'Builds credibility' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Festival or local campaign content', benefit: 'Relevant marketing' },
        ],
    },
    {
        id: 'personal-brands',
        icon: '🧑‍💼',
        name: 'Personal Brands',
        desc: 'Creators, coaches, consultants',
        benefit: 'Built on trust, authority, and personality',
        videoTypes: [
            { id: 'pov-stories', name: 'POV Stories', desc: 'Relatable scenarios from first-person view', benefit: 'Highly shareable' },
            { id: 'talking-head', name: 'Talking Head Videos', desc: 'Direct communication to audience', benefit: 'Builds authority' },
            { id: 'quick-hacks-tips', name: 'Quick Hacks / Tips Videos', desc: 'Actionable tips and insights', benefit: 'Drives engagement' },
            { id: 'mini-story', name: 'Mini Story Videos', desc: 'Short narrative-driven content', benefit: 'Emotional connection' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Expert insights and opinions', benefit: 'Builds credibility' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Client/student success stories', benefit: 'Proof of results' },
            { id: 'commercial-personal-brand', name: 'Commercial / Personal Brand Film', desc: 'Story-driven brand introduction', benefit: 'Strong positioning' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Launch or niche content', benefit: 'Flexibility' },
        ],
    },
    {
        id: 'fitness-brands',
        icon: '🏋️',
        name: 'Fitness Brands',
        desc: 'Fitness and training programs',
        benefit: 'Driven by results, motivation, and proof',
        videoTypes: [
            { id: 'transform-videos', name: 'Transform Videos', desc: 'Before → after body/results transformation', benefit: 'High motivation + conversion' },
            { id: 'demo-walkthrough', name: 'Demo Walkthrough Videos', desc: 'Exercise routines and guidance', benefit: 'Practical value' },
            { id: 'pov-stories', name: 'POV Stories', desc: 'Daily fitness scenarios', benefit: 'Relatable' },
            { id: 'challenge-videos', name: 'Challenge Videos', desc: 'Fitness programs and challenges', benefit: 'Engagement' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Trainer-led advice', benefit: 'Builds trust' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Client transformation stories', benefit: 'Strong proof' },
            { id: 'commercial-fitness-ad', name: 'Commercial / Fitness Ad', desc: 'Transformation storytelling', benefit: 'Aspirational appeal' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Program-specific content', benefit: 'Targeted campaigns' },
        ],
    },
    {
        id: 'food-brands',
        icon: '🍔',
        name: 'Food Brands',
        desc: 'Restaurants, cafes, packaged food',
        benefit: 'Driven by visual appeal and cravings',
        videoTypes: [
            { id: 'satisfying-asmr', name: 'Satisfying / ASMR Videos', desc: 'Close-up food visuals and textures', benefit: 'High watch time' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Scroll-stopping food shots', benefit: 'Discovery' },
            { id: 'bts-videos', name: 'BTS Videos', desc: 'Cooking and preparation process', benefit: 'Authenticity' },
            { id: 'product-demo-videos', name: 'Product Demo Videos', desc: 'Dish presentation and serving', benefit: 'Drives orders' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer reactions and reviews', benefit: 'Builds trust' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Deals and combos', benefit: 'Immediate sales' },
            { id: 'commercial-food-ad', name: 'Commercial / Food Ad', desc: 'Cinematic storytelling', benefit: 'Craving + recall' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Seasonal menu content', benefit: 'Timely marketing' },
        ],
    },
    {
        id: 'real-estate',
        icon: '🏠',
        name: 'Real Estate',
        desc: 'Property and projects',
        benefit: 'High-ticket decisions need trust + visualization',
        videoTypes: [
            { id: 'experience-videos', name: 'Experience Videos', desc: 'Full property walkthrough experience', benefit: 'Core conversion' },
            { id: 'lifestyle-videos', name: 'Lifestyle Videos', desc: 'Shows living experience', benefit: 'Emotional appeal' },
            { id: 'pov-stories', name: 'POV Stories', desc: 'First-person property tours', benefit: 'Immersive engagement' },
            { id: 'comparison-videos', name: 'Comparison Videos', desc: 'Property comparisons', benefit: 'Decision clarity' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Buyer experiences', benefit: 'Builds trust' },
            { id: 'cinematic-commercial', name: 'Cinematic Commercial / Promo Videos', desc: 'High-end property storytelling', benefit: 'Premium positioning' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Project deals and offers', benefit: 'Lead generation' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Project-specific campaigns', benefit: 'Flexibility' },
        ],
    },
    {
        id: 'saas-products',
        icon: '💻',
        name: 'SaaS Products',
        desc: 'Software and digital products',
        benefit: 'Complex products need clarity + education',
        videoTypes: [
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'What the product does and why it matters', benefit: 'Reduces confusion' },
            { id: 'demo-walkthrough', name: 'Demo Walkthrough Videos', desc: 'Step-by-step product usage', benefit: 'Drives signups' },
            { id: 'problem-solution', name: 'Problem-Solution Videos', desc: 'Pain → solution narrative', benefit: 'Conversion-focused' },
            { id: 'comparison-videos', name: 'Comparison Videos', desc: 'Vs competitors', benefit: 'Faster decisions' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer success stories', benefit: 'Builds credibility' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Industry insights', benefit: 'Builds trust' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Brand + product storytelling', benefit: 'Homepage conversion' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Onboarding/tutorial content', benefit: 'Product adoption' },
        ],
    },
    {
        id: 'edtech-brands',
        icon: '🎓',
        name: 'EdTech Brands',
        desc: 'Online education and courses',
        benefit: 'Trust + results-driven learning',
        videoTypes: [
            { id: 'mini-lessons', name: 'Mini Lessons', desc: 'Short teaching content', benefit: 'Builds trust' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Expert-led content', benefit: 'Positioning' },
            { id: 'quick-hacks-tips', name: 'Quick Hacks / Tips Videos', desc: 'Learning shortcuts', benefit: 'Engagement' },
            { id: 'course-promo', name: 'Course Promo Videos', desc: 'Program highlights', benefit: 'Enrollment' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Student success stories', benefit: 'Strong conversion' },
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'Course or concept breakdown', benefit: 'Clarity' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Transformation storytelling', benefit: 'Emotional appeal' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Webinars/series', benefit: 'Expansion' },
        ],
    },
    {
        id: 'travel-brands',
        icon: '✈️',
        name: 'Travel Brands',
        desc: 'Travel, tourism, destinations',
        benefit: 'Driven by emotion and aspiration',
        videoTypes: [
            { id: 'experience-videos', name: 'Experience Videos', desc: 'Immersive travel experiences', benefit: 'Drives desire' },
            { id: 'cinematic-commercial', name: 'Cinematic Commercial / Promo Videos', desc: 'Destination storytelling', benefit: 'Bookings' },
            { id: 'pov-stories', name: 'POV Stories', desc: 'First-person travel content', benefit: 'Engagement' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Quick destination highlights', benefit: 'Discovery' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Traveler experiences', benefit: 'Builds trust' },
            { id: 'mini-story', name: 'Mini Story Videos', desc: 'Journey-based storytelling', benefit: 'Recall' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Travel deals', benefit: 'Conversions' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Seasonal campaigns', benefit: 'Flexibility' },
        ],
    },
    {
        id: 'hospitality-brands',
        icon: '🏨',
        name: 'Hospitality Brands',
        desc: 'Hotels, resorts, stays',
        benefit: 'Selling experiences, not just services',
        videoTypes: [
            { id: 'experience-videos', name: 'Experience Videos', desc: 'Hotel/resort walkthrough', benefit: 'Conversion' },
            { id: 'lifestyle-videos', name: 'Lifestyle Videos', desc: 'Stay experience visuals', benefit: 'Desire' },
            { id: 'bts-videos', name: 'BTS Videos', desc: 'Service preparation', benefit: 'Trust' },
            { id: 'pov-stories', name: 'POV Stories', desc: 'Guest experience', benefit: 'Relatable' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Guest reviews', benefit: 'Credibility' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Luxury storytelling', benefit: 'Brand positioning' },
            { id: 'offer-videos', name: 'Offer Videos', desc: 'Stay deals', benefit: 'Bookings' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Event promotions', benefit: 'Additional revenue' },
        ],
    },
    {
        id: 'healthcare-brands',
        icon: '🏥',
        name: 'Healthcare Brands',
        desc: 'Clinics, hospitals, med-tech',
        benefit: 'Trust is the most critical factor',
        videoTypes: [
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'Medical services explained simply', benefit: 'Builds trust' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Doctor-led insights', benefit: 'Credibility' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Patient experiences', benefit: 'Emotional trust' },
            { id: 'mini-story', name: 'Mini Story Videos', desc: 'Recovery journeys', benefit: 'Engagement' },
            { id: 'demo-walkthrough', name: 'Demo Walkthrough Videos', desc: 'Procedure explanations', benefit: 'Reduces fear' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Care-focused storytelling', benefit: 'Confidence' },
            { id: 'educational-videos', name: 'Educational Videos', desc: 'Health awareness content', benefit: 'Authority' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Campaign-specific content', benefit: 'Custom messaging' },
        ],
    },
    {
        id: 'home-interiors',
        icon: '🏡',
        name: 'Home & Interiors',
        desc: 'Furniture, decor, interior design',
        benefit: 'Visual transformation drives decisions',
        videoTypes: [
            { id: 'transform-videos', name: 'Transform Videos', desc: 'Before → after design transformation', benefit: 'High impact' },
            { id: 'experience-videos', name: 'Experience Videos', desc: 'Space walkthroughs', benefit: 'Visualization' },
            { id: 'bts-videos', name: 'BTS Videos', desc: 'Design process', benefit: 'Trust' },
            { id: 'product-showcase-videos', name: 'Product Showcase Videos', desc: 'Furniture/decor display', benefit: 'Purchase' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Client feedback', benefit: 'Credibility' },
            { id: 'lifestyle-videos', name: 'Lifestyle Videos', desc: 'Designed space in use', benefit: 'Inspiration' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Lifestyle storytelling', benefit: 'Premium positioning' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Custom campaigns', benefit: 'Flexibility' },
        ],
    },
    {
        id: 'services',
        icon: '🧾',
        name: 'Services',
        desc: 'Agencies, consultancies, pros',
        benefit: 'Sell expertise and results',
        videoTypes: [
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'Service breakdown', benefit: 'Clarity' },
            { id: 'case-study-videos', name: 'Case Study Videos', desc: 'Client results', benefit: 'Proof' },
            { id: 'authority-videos', name: 'Authority Videos', desc: 'Insights and expertise', benefit: 'Trust' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Client feedback', benefit: 'Credibility' },
            { id: 'problem-solution', name: 'Problem-Solution Videos', desc: 'Pain → solution', benefit: 'Conversion' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Brand storytelling', benefit: 'Lead generation' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Awareness content', benefit: 'Reach' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Custom pitches', benefit: 'Versatility' },
        ],
    },
    {
        id: 'industrial-manufacturing',
        icon: '🏭',
        name: 'Industrial & Manufacturing',
        desc: 'Machinery, equipment, electronics, factories',
        benefit: 'Driven by trust, reliability, and B2B credibility',
        videoTypes: [
            { id: 'product-demo-videos', name: 'Product Demo Videos', desc: 'Show how a machine, tool or component works', benefit: 'Shortens B2B sales cycles' },
            { id: 'factory-tour-videos', name: 'Factory / Facility Tour', desc: 'Walkthrough of the plant, process or production line', benefit: 'Builds trust at scale' },
            { id: 'case-study-videos', name: 'Case Study Videos', desc: 'Real customer deployments and measurable outcomes', benefit: 'Strong proof for procurement teams' },
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'How the technology / process works in plain language', benefit: 'Educates non-technical decision-makers' },
            { id: 'safety-training-videos', name: 'Safety / Training Videos', desc: 'Operating procedures, safety briefs, on-site SOPs', benefit: 'Internal enablement & compliance' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'High-quality brand films for the company', benefit: 'Brand authority & RFP wins' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Buyer / partner endorsements', benefit: 'Trust signal for B2B prospects' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Trade-show reels, ESG stories, recruitment films', benefit: 'Flexible use cases' },
        ],
    },
    {
        id: 'events-luxury',
        icon: '💍',
        name: 'Events & Luxury',
        desc: 'Events, weddings, luxury goods',
        benefit: 'Driven by emotion, experience, and exclusivity',
        videoTypes: [
            { id: 'cinematic-highlight', name: 'Cinematic Highlight Films', desc: 'Event storytelling', benefit: 'Premium appeal' },
            { id: 'mini-story', name: 'Mini Story Videos', desc: 'Emotional narratives', benefit: 'Recall' },
            { id: 'experience-videos', name: 'Experience Videos', desc: 'Luxury experiences', benefit: 'Desire' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Client feedback', benefit: 'Trust' },
            { id: 'teaser-videos', name: 'Teaser Videos', desc: 'Pre-event hype', benefit: 'Anticipation' },
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'Aspirational storytelling', benefit: 'High-ticket appeal' },
            { id: 'bts-videos', name: 'BTS Videos', desc: 'Event preparation', benefit: 'Authenticity' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Exclusive campaigns', benefit: 'Custom branding' },
        ],
    },
    // "Other (custom industry)" — last entry, intentionally a catch-all.
    // When the user picks this, the picker reveals a free-text input where
    // they can type their own industry label (capped at 40 chars). The
    // typed label is swapped into the request body's `industry.name` so the
    // LLM gets the user's exact wording.
    {
        id: 'other-industry',
        icon: '✨',
        name: 'Other',
        desc: 'Type your own industry',
        benefit: 'Use when none of the above categories fit your brand',
        videoTypes: [
            { id: 'commercial-promo', name: 'Commercial / Promo Videos', desc: 'High-quality brand storytelling', benefit: 'Builds brand value' },
            { id: 'product-demo-videos', name: 'Product Demo Videos', desc: 'Show the product / service in action', benefit: 'Drives consideration' },
            { id: 'hook-trend-reels', name: 'Hook / Trend Reels', desc: 'Trend-driven short videos', benefit: 'Maximises reach' },
            { id: 'testimonial-videos', name: 'Testimonial Videos', desc: 'Customer / client endorsements', benefit: 'Builds trust' },
            { id: 'explainer-videos', name: 'Explainer Videos', desc: 'Educational walk-through of the idea', benefit: 'Clears confusion' },
            { id: 'ugc-ads', name: 'UGC Ads', desc: 'Shot-on-phone style ads', benefit: 'High-performing ads' },
            { id: 'lifestyle-videos', name: 'Lifestyle Videos', desc: 'The product / service inside everyday life', benefit: 'Relatable & desirable' },
            { id: 'other-custom', name: 'Other (Custom Video)', desc: 'Anything outside the formats above', benefit: 'Maximum creative flexibility' },
        ],
    },
];

// =============================================================================
// Auto-suggest industry + video type from a Gemini video-analysis result
// =============================================================================
// Lightweight keyword scorer. We score each industry and each video type on
// how many of its representative keywords appear in the analysis (title +
// summary + highlights + visual/audio style + scene descriptions), then pick
// the highest-scoring pair. The user can always change the selection.

// Per-industry synonym set used for matching — augments the catalog's `name`,
// `desc`, and `benefit` text. Keep entries lowercase, single-word or short
// phrase, focused on words that uniquely identify the vertical.
const INDUSTRY_KEYWORDS = {
    'd2c-commerce': ['product', 'shop', 'shopping', 'ecommerce', 'cart', 'buy', 'launch', 'dtc', 'retail', 'consumer'],
    'fashion-brands': ['fashion', 'outfit', 'clothing', 'apparel', 'wear', 'wardrobe', 'dress', 'shirt', 'jeans', 'streetwear', 'style', 'lookbook', 'runway', 'model'],
    'beauty-brands': ['beauty', 'skincare', 'makeup', 'cosmetic', 'cosmetics', 'cream', 'serum', 'lotion', 'foundation', 'lipstick', 'mascara', 'glow', 'complexion', 'salon'],
    'local-businesses': ['local', 'neighborhood', 'community', 'shop', 'cafe', 'boutique', 'storefront', 'town'],
    'personal-brands': ['creator', 'influencer', 'personal brand', 'coach', 'speaker', 'host', 'author', 'expert'],
    'fitness-brands': ['fitness', 'workout', 'gym', 'exercise', 'training', 'muscle', 'cardio', 'yoga', 'pilates', 'athletic', 'sports', 'fit'],
    'food-brands': ['food', 'recipe', 'meal', 'cooking', 'restaurant', 'taste', 'dish', 'kitchen', 'snack', 'beverage', 'drink', 'chef', 'cuisine', 'ingredient'],
    'real-estate': ['real estate', 'property', 'apartment', 'house', 'home tour', 'listing', 'realtor', 'condo', 'villa', 'broker'],
    'saas-products': ['saas', 'software', 'app', 'platform', 'dashboard', 'workflow', 'tool', 'productivity', 'integration', 'api', 'tech'],
    'edtech-brands': ['education', 'learning', 'course', 'lesson', 'student', 'tutorial', 'classroom', 'edtech', 'study', 'teacher', 'school'],
    'travel-brands': ['travel', 'vacation', 'destination', 'tour', 'trip', 'journey', 'adventure', 'beach', 'mountain', 'flight', 'airline'],
    'hospitality-brands': ['hotel', 'resort', 'stay', 'hospitality', 'lobby', 'suite', 'concierge', 'boutique hotel', 'lodging'],
    'healthcare-brands': ['health', 'healthcare', 'medical', 'doctor', 'clinic', 'patient', 'wellness', 'therapy', 'pharma', 'hospital', 'dental'],
    'home-interiors': ['home', 'interior', 'decor', 'furniture', 'living room', 'kitchen design', 'renovation', 'design', 'aesthetic'],
    'services': ['service', 'agency', 'consultant', 'professional', 'b2b', 'lawyer', 'accountant', 'cleaning', 'repair'],
    'events-luxury': ['event', 'wedding', 'gala', 'luxury', 'premium', 'exclusive', 'red carpet', 'celebration', 'party'],
    'industrial-manufacturing': ['industrial', 'manufacturing', 'factory', 'machinery', 'equipment', 'electronics', 'production line', 'plant', 'automation', 'robotics', 'cnc', 'b2b', 'engineering', 'industry 4.0', 'assembly line', 'fabrication', 'foundry', 'warehouse'],
    // NOTE: 'other-industry' is the catch-all; we INTENTIONALLY do NOT
    // give it keywords so the auto-suggest from video analysis never picks
    // it over a more specific match.
};

// Per-video-type synonym set. Keys are video-type IDs (which can repeat across
// industries — e.g. ugc-ads exists in d2c-commerce AND beauty-brands). We
// match on ID once, regardless of which industry it belongs to.
const VIDEOTYPE_KEYWORDS = {
    'ugc-ads': ['ugc', 'user generated', 'phone', 'real person', 'authentic', 'selfie', 'review'],
    'hook-trend-reels': ['trend', 'viral', 'meme', 'tiktok', 'reel', 'shorts', 'fast cut', 'pattern interrupt'],
    'transform-videos': ['before', 'after', 'transform', 'transformation', 'reveal', 'glow up'],
    'product-demo-videos': ['demo', 'how it works', 'feature', 'walk through', 'walkthrough', 'showcase'],
    'product-showcase-videos': ['showcase', 'studio', 'product display', 'turntable'],
    'product-service-demo': ['service demo', 'in-store', 'process', 'service walkthrough'],
    'testimonial-videos': ['testimonial', 'customer review', 'real user', 'success story', 'happy customer', 'client review'],
    'offer-videos': ['offer', 'discount', 'sale', 'limited time', 'deal', 'coupon', 'promo'],
    'commercial-promo': ['commercial', 'advertisement', 'cinematic', 'high production', 'brand film'],
    'commercial-fashion-film': ['fashion film', 'cinematic', 'editorial', 'campaign film', 'high fashion'],
    'commercial-beauty-ad': ['beauty ad', 'cinematic beauty', 'glamour', 'commercial'],
    'commercial-fitness-ad': ['fitness ad', 'transformation story', 'aspirational fitness'],
    'commercial-food-ad': ['food ad', 'cinematic food', 'craving'],
    'commercial-personal-brand': ['brand film', 'personal brand', 'introduction'],
    'cinematic-commercial': ['cinematic', 'high-end', 'premium commercial', 'film'],
    'tutorial-videos': ['tutorial', 'how to', 'step by step', 'guide', 'walkthrough', 'recipe', 'cooking'],
    'satisfying-asmr': ['asmr', 'satisfying', 'close up', 'macro', 'texture', 'silent', 'no dialogue', 'tactile'],
    'lookbook-videos': ['lookbook', 'fashion shoot', 'styled', 'editorial', 'styled outfits'],
    'lifestyle-videos': ['lifestyle', 'day in the life', 'real life', 'aspirational', 'living'],
    'bts-videos': ['behind the scenes', 'bts', 'making of', 'preparation'],
    'experience-videos': ['property tour', 'home tour', 'house tour', 'walkthrough', 'tour', 'experience', 'full tour'],
    'explainer-videos': ['explainer', 'animated', 'whiteboard', 'motion graphic', 'what is', 'why'],
    'demo-walkthrough': ['demo', 'walkthrough', 'walk through', 'product walkthrough', 'feature walkthrough', 'how it works'],
    'problem-solution': ['problem', 'pain point', 'solution', 'fix', 'struggle'],
    'comparison-videos': ['vs', 'comparison', 'compared to', 'which is better', 'a vs b'],
    'pov-stories': ['pov', 'first person', 'point of view'],
    'talking-head': ['talking head', 'direct to camera', 'interview', 'speaker'],
    'quick-hacks-tips': ['hack', 'tip', 'tips', 'trick', 'shortcut'],
    'mini-story': ['mini story', 'narrative', 'short story', 'story-driven'],
    'authority-videos': ['expert', 'authority', 'specialist', 'opinion', 'industry insight'],
    'mini-lessons': ['lesson', 'mini lesson', 'teaching', 'educational'],
    'course-promo': ['course', 'enrollment', 'program', 'curriculum'],
    'challenge-videos': ['challenge', 'program', '30 days', '21 days'],
    'other-custom': [], // never auto-pick generic fallback
};

const _scoreText = (text, haystack, haystackTokens) => {
    if (!text) return 0;
    const tokens = (text || '').toLowerCase().match(/[a-z]{3,}/g) || [];
    let score = 0;
    for (const t of tokens) {
        if (haystackTokens.has(t)) score += 1;
    }
    // phrase-level substring (catches multi-word entries like "real estate")
    if (text.length > 4 && haystack.includes(text.toLowerCase())) score += 2;
    return score;
};

/**
 * Given a Gemini video-analysis result, suggest the closest industry + video
 * type from the catalog. Returns { industry, videoType, confidence } where
 * `industry` and `videoType` are the catalog objects (may be null if nothing
 * matched at all — caller should fall back to manual selection).
 */
export const suggestFromVideoAnalysis = (analysis) => {
    if (!analysis) return { industry: null, videoType: null, confidence: 0 };

    // Build the "haystack" — every text field Gemini gives us about the video.
    const parts = [
        analysis.title,
        analysis.text,
        ...(analysis.highlights || []),
        analysis.visualStyle?.mood,
        analysis.visualStyle?.pacing,
        analysis.visualStyle?.lighting,
        analysis.audioStyle?.music_genre,
        analysis.audioStyle?.voice_tone,
        analysis.transcript?.text?.slice(0, 800),
        ...(analysis.segments || []).flatMap(s => [s.description, s.dominant_visual, s.audio_summary]),
    ];
    const haystack = parts.filter(Boolean).join(' ').toLowerCase();
    if (!haystack) return { industry: null, videoType: null, confidence: 0 };
    const haystackTokens = new Set(haystack.match(/[a-z]{3,}/g) || []);

    // ---- Score each industry ----
    let bestIndustry = null;
    let bestIndustryScore = 0;
    for (const ind of CONCEPT_CATALOG) {
        // weights: keyword set > name > desc > benefit
        const kw = (INDUSTRY_KEYWORDS[ind.id] || []).reduce((acc, k) => acc + _scoreText(k, haystack, haystackTokens), 0) * 3;
        const name = _scoreText(ind.name, haystack, haystackTokens) * 2;
        const desc = _scoreText(ind.desc, haystack, haystackTokens);
        const benefit = _scoreText(ind.benefit, haystack, haystackTokens);
        const total = kw + name + desc + benefit;
        if (total > bestIndustryScore) {
            bestIndustryScore = total;
            bestIndustry = ind;
        }
    }
    // Sensible default if nothing scored — D2C Commerce is the most generic
    if (!bestIndustry) bestIndustry = CONCEPT_CATALOG[0];

    // ---- Score each video type WITHIN the chosen industry ----
    let bestVideoType = null;
    let bestVideoTypeScore = 0;
    for (const vt of bestIndustry.videoTypes) {
        if (vt.id === 'other-custom') continue;  // never auto-pick the catch-all
        const kw = (VIDEOTYPE_KEYWORDS[vt.id] || []).reduce((acc, k) => acc + _scoreText(k, haystack, haystackTokens), 0) * 3;
        const name = _scoreText(vt.name, haystack, haystackTokens) * 2;
        const desc = _scoreText(vt.desc, haystack, haystackTokens);
        const benefit = _scoreText(vt.benefit, haystack, haystackTokens);
        const total = kw + name + desc + benefit;
        if (total > bestVideoTypeScore) {
            bestVideoTypeScore = total;
            bestVideoType = vt;
        }
    }
    if (!bestVideoType) bestVideoType = bestIndustry.videoTypes[0];

    // Confidence = 0..1, useful for the UI to differentiate "good guess"
    // from "best of a bad lot".
    const confidence = Math.min(1, (bestIndustryScore + bestVideoTypeScore) / 30);

    return { industry: bestIndustry, videoType: bestVideoType, confidence };
};

// =============================================================================
// Auto-suggest industry from a saved-brand payload (BrandScopePicker.ctx.brand)
// =============================================================================
// Feb 2026 — When the user applies a saved brand on the Story step we already
// know a lot about the brand (name, description, category, tone of voice,
// products, taglines). This picks the closest catalog industry so Step 3
// (Concept) can pre-select the Industry tile and let the user jump straight
// to Video Type. Uses the same weighted keyword scorer as the video-analysis
// matcher so the two agree on ambiguous verticals.
//
// The `brand` argument is the raw `ctx.brand` from
//   GET /api/brands/{id}/context
// but we accept anything shaped like `{ name, description, industry, category,
// profile: { ... }, products: [...] }` and defensively pluck every text field
// we can find. Missing fields are silently ignored.
export const suggestIndustryFromBrand = (brand, extras = {}) => {
    if (!brand && !extras) return { industry: null, confidence: 0 };

    // Recursively collect every string value from the brand + extras object
    // graph, up to a shallow depth so we don't slurp entire product image
    // URLs into the haystack. Keeps the scorer honest.
    const collectStrings = (node, depth = 0, out = []) => {
        if (!node || depth > 4) return out;
        if (typeof node === 'string') { if (node.length < 400) out.push(node); return out; }
        if (Array.isArray(node)) { node.forEach((n) => collectStrings(n, depth + 1, out)); return out; }
        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                // Skip obvious non-text fields to keep the haystack focused.
                if (/(_?url|_?src|image|logo|thumbnail|asset|id|_id|uuid|slug|createdAt|updatedAt|timestamp)/i.test(k)) continue;
                collectStrings(v, depth + 1, out);
            }
        }
        return out;
    };

    const parts = [
        ...collectStrings(brand || {}),
        ...collectStrings(extras || {}),
    ];
    const haystack = parts.filter(Boolean).join(' ').toLowerCase();
    if (!haystack) return { industry: null, confidence: 0 };
    const haystackTokens = new Set(haystack.match(/[a-z]{3,}/g) || []);

    let bestIndustry = null;
    let bestScore = 0;
    for (const ind of CONCEPT_CATALOG) {
        if (ind.id === 'other-industry') continue;   // never auto-pick the catch-all
        const kw      = (INDUSTRY_KEYWORDS[ind.id] || []).reduce((acc, k) => acc + _scoreText(k, haystack, haystackTokens), 0) * 3;
        const name    = _scoreText(ind.name,    haystack, haystackTokens) * 2;
        const desc    = _scoreText(ind.desc,    haystack, haystackTokens);
        const benefit = _scoreText(ind.benefit, haystack, haystackTokens);
        const total   = kw + name + desc + benefit;
        if (total > bestScore) { bestScore = total; bestIndustry = ind; }
    }
    // No match at all → return null so the caller can leave the picker
    // untouched (better than a wrong auto-select for a niche brand).
    const confidence = Math.min(1, bestScore / 12);
    return { industry: bestIndustry, confidence };
};
